import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { uploadFile } from '../api/backend'
import { useAuth } from '../contexts/AuthContext'
import type { ChatAttachment } from '../state/types'
import { generateId } from '../state/types'

const TEXT_FILE_EXTENSIONS = new Set([
  'pdf',
  'txt',
  'md',
  'json',
  'csv',
  'xml',
  'html',
  'js',
  'ts',
  'tsx',
  'jsx',
  'py',
  'java',
  'c',
  'cpp',
  'go',
  'rs',
  'sh',
  'yaml',
  'yml',
  'toml',
  'ini',
  'cfg',
  'log',
  'sql',
])

export const ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/avif,image/bmp,application/pdf,.pdf,.txt,.md,.json,.csv,.xml,.html,.js,.ts,.tsx,.jsx,.py,.java,.c,.cpp,.go,.rs,.sh,.yaml,.yml,.toml,.ini,.cfg,.log,.sql'

function getFileExtension(name: string): string {
  const parts = name.toLowerCase().split('.')
  return parts.length > 1 ? parts[parts.length - 1] ?? '' : ''
}

function isSvgFile(file: File): boolean {
  return file.type === 'image/svg+xml' || getFileExtension(file.name) === 'svg'
}

function isSupportedAttachment(file: File): boolean {
  if (isSvgFile(file)) return false
  if (file.type.startsWith('image/')) return true
  if (file.type === 'application/pdf') return true
  if (file.type.startsWith('text/')) return true
  if (file.type === 'application/json' || file.type === 'application/xml' || file.type === 'application/sql') return true
  return TEXT_FILE_EXTENSIONS.has(getFileExtension(file.name))
}

function createPendingAttachment(file: File): ChatAttachment {
  return {
    id: generateId(),
    name: file.name,
    type: file.type || 'application/octet-stream',
    size: file.size,
    url: URL.createObjectURL(file),
    encoding: file.type.startsWith('image/') ? 'base64' : 'text',
    uploadStatus: 'uploading',
    uploadProgress: 0,
  }
}

export function useAttachmentUpload() {
  const { getAccessToken } = useAuth()
  const [attachments, setAttachments] = useState<ChatAttachment[]>([])
  const abortControllersRef = useRef(new Map<string, AbortController>())

  const isUploadingAttachments = useMemo(
    () => attachments.some((attachment) => attachment.uploadStatus === 'uploading'),
    [attachments],
  )

  const addFiles = useCallback(async (files: File[]) => {
    const token = getAccessToken()
    if (!token) {
      toast.error('You need to sign in before uploading attachments.')
      return
    }

    const supportedFiles = files.filter(isSupportedAttachment)
    if (supportedFiles.length !== files.length) {
      toast.error('Only non-SVG image, PDF, and text attachments are supported.')
    }
    if (supportedFiles.length === 0) return

    const pendingAttachments = supportedFiles.map(createPendingAttachment)
    setAttachments((prev) => [...prev, ...pendingAttachments])

    await Promise.all(
      supportedFiles.map(async (file, index) => {
        const pendingAttachment = pendingAttachments[index]
        const controller = new AbortController()
        abortControllersRef.current.set(pendingAttachment.id, controller)

        try {
          const stored = await uploadFile(token, file, {
            signal: controller.signal,
            onProgress: (percent) => {
              setAttachments((prev) =>
                prev.map((attachment) =>
                  attachment.id === pendingAttachment.id
                    ? { ...attachment, uploadProgress: percent }
                    : attachment,
                ),
              )
            },
          })

          setAttachments((prev) =>
            prev.map((attachment) =>
              attachment.id === pendingAttachment.id
                ? {
                    ...attachment,
                    storageId: stored.id,
                    storageName: stored.storageName,
                    uploadStatus: 'uploaded',
                    uploadProgress: 100,
                  }
                : attachment,
            ),
          )
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return
          }

          const message = error instanceof Error ? error.message : 'Failed to upload attachment'
          toast.error(message)
          setAttachments((prev) =>
            prev.map((attachment) =>
              attachment.id === pendingAttachment.id
                ? { ...attachment, uploadStatus: 'failed', uploadProgress: 0 }
                : attachment,
            ),
          )
        } finally {
          abortControllersRef.current.delete(pendingAttachment.id)
        }
      }),
    )
  }, [getAccessToken])

  const removeAttachment = useCallback((id: string) => {
    const controller = abortControllersRef.current.get(id)
    if (controller) {
      controller.abort()
      abortControllersRef.current.delete(id)
    }

    setAttachments((prev) => {
      const attachment = prev.find((item) => item.id === id)
      if (attachment) URL.revokeObjectURL(attachment.url)
      return prev.filter((item) => item.id !== id)
    })
  }, [])

  const clearAttachments = useCallback(() => {
    abortControllersRef.current.forEach((controller) => controller.abort())
    abortControllersRef.current.clear()
    setAttachments((prev) => {
      prev.forEach((attachment) => URL.revokeObjectURL(attachment.url))
      return []
    })
  }, [])

  return {
    attachments,
    setAttachments,
    addFiles,
    removeAttachment,
    clearAttachments,
    isUploadingAttachments,
  }
}
